import { useEffect, useState } from "react";
import { Mic, Youtube, Podcast as SpotifyIcon, Apple, Play } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiError } from "../lib/api";
import SEO from "../components/SEO";
import Divider from "../components/Divider";
import { Reveal } from "../components/Reveal";

const CHANNEL_URL = "https://youtube.com/@dentroilcalice";
const SPOTIFY_URL = "https://open.spotify.com/show/5ooaVLhqNwDfsShK2FUttI";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
}

const FALLBACK_VIDEOS = [
  { id: "yAwQ13yzH5Y", title: "Dentro il Calice — Intervista a Fabio Codognola" },
  { id: "fWoE_7Drexw", title: "Dentro il Calice — Intervista a Massimo Perna" },
  { id: "xNjfwu_ssK8", title: "Dentro il Calice — Intervista a Gianpaolo Spinelli" },
  { id: "e6RCo-HwGxA", title: "Dentro il Calice — Intervista a Luca Nicolis" },
  { id: "cPOPH8WzoqM", title: "Dentro il Calice — Intervista a Leonardo Ferrari" },
  { id: "_4A9Xy-DOa4", title: "Dentro il Calice — Intervista a Stefano Mangiarotti" },
  { id: "2Bjp5XF5pIs", title: "Dentro il Calice — Intervista a Stefano Canello" },
  { id: "bfYSng1MA8E", title: 'Dentro il Calice — Az. Agricola "Torre di Terzolan"' },
  { id: "JphZK9AONdQ", title: 'Dentro il Calice — Azienda "Monte Cimo"' },
];

function LiteYouTube({ id, title, testId }) {
  const [play, setPlay] = useState(false);
  if (play) {
    return (
      <iframe
        data-testid={`${testId}-iframe`}
        className="absolute inset-0 h-full w-full"
        src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }
  return (
    <button
      data-testid={testId}
      onClick={() => setPlay(true)}
      aria-label={`Riproduci: ${title}`}
      className="group absolute inset-0 h-full w-full"
    >
      <img
        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
        alt={title}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <span className="absolute inset-0 bg-nero/30 group-hover:bg-nero/20 transition-colors" />
      <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-bordeaux/90 text-panna shadow-xl transition-transform duration-300 group-hover:scale-110">
        <Play size={26} className="ml-1 fill-panna" />
      </span>
    </button>
  );
}

export default function Podcast() {
  const [guest, setGuest] = useState({ name: "", email: "", message: "", privacy: false });
  const [sent, setSent] = useState(false);
  const [videos, setVideos] = useState(FALLBACK_VIDEOS);

  useEffect(() => {
    client.get("/podcast/videos")
      .then((r) => { if (r.data?.videos?.length) setVideos(r.data.videos); })
      .catch(() => {});
  }, []);

  const featured = videos[0];
  const rest = videos.slice(1);

  const submitGuest = async (e) => {
    e.preventDefault();
    if (!guest.privacy) { toast.error("Devi accettare la Privacy Policy."); return; }
    try {
      await client.post("/contacts", { ...guest, reason: "Ospite podcast" });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="podcast-page">
      <SEO title="Podcast" path="/podcast"
           description="Dentro il Calice — Il Podcast: interviste e storie di vino, persone e territori. Guarda gli episodi video." />

      {/* HERO */}
      <section className="bg-nero pt-40 pb-20 grain relative">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 text-center">
          <Reveal>
            <Mic size={28} className="text-oro mx-auto mb-6" />
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-panna">Dentro il Calice — Il Podcast</h1>
            <p className="mt-6 text-panna/70 leading-relaxed max-w-2xl mx-auto">
              Interviste e storie di vino, persone e territori. Un ospite, un calice, una storia vera.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a href={CHANNEL_URL} target="_blank" rel="noopener noreferrer" data-testid="platform-youtube"
                 className="inline-flex items-center gap-2 rounded-full bg-oro px-6 py-3 text-nero text-sm font-semibold hover:bg-oro-light transition-colors">
                <Youtube size={16} /> Guarda su YouTube
              </a>
              <a href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer" data-testid="platform-spotify"
                 className="inline-flex items-center gap-2 rounded-full border border-oro/60 px-6 py-3 text-oro text-sm font-semibold hover:bg-oro/10 transition-colors">
                <SpotifyIcon size={16} /> Ascolta su Spotify
              </a>
              <span data-testid="platform-apple-podcasts"
                    className="inline-flex items-center gap-2 rounded-full border border-oro/40 px-6 py-3 text-oro/80 text-sm"
                    title="Apple Podcasts — in arrivo">
                <Apple size={16} /> Apple Podcasts · in arrivo
              </span>
            </div>
            <Divider light className="mt-12" />
          </Reveal>
        </div>
      </section>

      {/* EPISODIO IN EVIDENZA */}
      <section className="bg-nero pb-20" data-testid="section-featured">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <p className="text-oro text-xs font-semibold uppercase tracking-[0.25em] mb-4">Episodio in evidenza</p>
            <div className="relative w-full aspect-video overflow-hidden rounded-2xl border border-oro/20 shadow-2xl shadow-black/50">
              <LiteYouTube id={featured.id} title={featured.title} testId="featured-player" />
            </div>
            <h2 className="mt-5 font-serif text-2xl font-semibold text-panna">{featured.title}</h2>
            {formatDate(featured.published_at) && (
              <p data-testid="featured-date" className="mt-1.5 text-panna/50 text-sm">
                Pubblicato il {formatDate(featured.published_at)}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {/* GRIGLIA EPISODI */}
      <section className="bg-panna py-20 sm:py-24" data-testid="section-episodes">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-nero text-center">Tutti gli episodi</h2>
          <p className="mt-3 text-nero/60 text-center text-sm">Guardali direttamente qui, senza uscire dal sito.</p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((v, i) => (
              <Reveal key={v.id} delay={(i % 3) * 0.1}>
                <article data-testid={`video-card-${i}`}
                         className="rounded-2xl border border-nero/10 bg-panna-dark/40 overflow-hidden h-full flex flex-col">
                  <div className="relative aspect-video bg-nero">
                    <LiteYouTube id={v.id} title={v.title} testId={`video-play-${i}`} />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-serif text-lg font-semibold text-nero leading-snug">{v.title}</h3>
                    {formatDate(v.published_at) && (
                      <p data-testid={`video-date-${i}`} className="mt-1.5 text-nero/45 text-xs flex-1">
                        {formatDate(v.published_at)}
                      </p>
                    )}
                    {!formatDate(v.published_at) && <div className="flex-1" />}
                    <a href={`https://youtu.be/${v.id}`} target="_blank" rel="noopener noreferrer"
                       className="mt-4 inline-flex items-center gap-1.5 text-bordeaux text-sm font-medium hover:gap-2.5 transition-all">
                      <Youtube size={15} /> Guarda su YouTube
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VUOI ESSERE OSPITE */}
      <section className="bg-bordeaux py-20 grain relative" data-testid="section-ospite">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal className="text-center mb-10">
            <h2 className="font-serif text-3xl font-bold text-panna">Vuoi essere ospite?</h2>
            <p className="mt-4 text-panna/75">Hai una storia di vino, persone o territorio da raccontare? Scrivici.</p>
          </Reveal>
          {sent ? (
            <p data-testid="guest-success" className="text-center font-serif text-xl text-oro">Grazie! Ti ricontatteremo presto.</p>
          ) : (
            <form onSubmit={submitGuest} data-testid="guest-form" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" required minLength={2} placeholder="Nome e cognome" data-testid="guest-name"
                       value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })}
                       className="rounded-lg px-4 py-3.5 bg-nero/40 border border-panna/20 text-panna placeholder:text-panna/40 focus:outline-none focus:ring-2 focus:ring-oro/60" />
                <input type="email" required placeholder="Email" data-testid="guest-email"
                       value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })}
                       className="rounded-lg px-4 py-3.5 bg-nero/40 border border-panna/20 text-panna placeholder:text-panna/40 focus:outline-none focus:ring-2 focus:ring-oro/60" />
              </div>
              <textarea required minLength={5} rows={3} placeholder="Raccontaci in due righe chi sei e la tua storia" data-testid="guest-message"
                        value={guest.message} onChange={(e) => setGuest({ ...guest, message: e.target.value })}
                        className="w-full rounded-lg px-4 py-3.5 bg-nero/40 border border-panna/20 text-panna placeholder:text-panna/40 focus:outline-none focus:ring-2 focus:ring-oro/60" />
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" data-testid="guest-privacy" checked={guest.privacy}
                       onChange={(e) => setGuest({ ...guest, privacy: e.target.checked })} className="mt-1 h-4 w-4 accent-[#B0832F]" />
                <span className="text-xs text-panna/70">Accetto la <a href="/privacy-policy" className="underline text-oro">Privacy Policy</a>.</span>
              </label>
              <button type="submit" data-testid="guest-submit"
                      className="rounded-full bg-nero text-oro border border-oro px-8 py-3.5 font-medium hover:bg-nero-light transition-colors">
                Proponiti come ospite
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
